import React from "react";
import Image from "next/image";
import NewPresence from "@/components/newPageComponent/cpa-new/main/new/NewPresence";

/* The source files are PNGs behind a .webp name, so `tr=f-webp` is a real
   byte saving; 700x420 is already ~2x the rendered card width, so no upscale
   is needed and every image stays lazy + explicitly sized (no CLS). */
const imageUrl = (name: string) =>
  `https://ik.imagekit.io/mileseducation/miles_website/seo-pages/cma-course/${name}.webp?tr=f-webp,q-80`;

const instituteCards = [
  {
    image: imageUrl("global"),
    title: "Access 100+ Global Educators & Industry Experts",
    description:
      "Beyond Varun Jain, learn from a wider faculty of specialists including performance-management author Gary Cokins and career coach Sherrika Sanders, bringing real industry perspective into every CMA class.",
  },
  {
    image: imageUrl("favourite_cma_instructor"),
    title: "Learn From the World’s Favourite CMA Instructor",
    description:
      "Engage directly with Varun Jain, CPA, CMA, a Harvard Business School alumnus who has personally guided 70,000+ students into global accounting careers.",
  },
  {
    image: imageUrl("toolkit"),
    title: "The Ultimate CMA Resource Toolkit",
    description:
      "Video lectures, e-books, 5,000+ MCQs, 100+ essays, simulations, and full-length mock exams, whether you’re doing a fully remote CMA online course or attending in person.",
  },
  {
    image: imageUrl("learning"),
    title: "Lifelong Learning",
    description:
      "Continue building your expertise with an annual Miles Masterclass subscription, featuring NASBA-approved CPEs, webinars, AI labs, and expert-led sessions for accounting professionals.",
  },
  {
    image: imageUrl("support"),
    title: "End-to-End Support",
    description:
      "IMA Diamond Partner discounts on membership, entrance, and exam fees, plus personalized one-on-one guidance at every stage.",
  },
];

const CmaCourseInstitute = () => {
  return (
    <>
      <section className="w-full bg-white md:py-[50px] max-md:bg-[#F3F3F3] pb-4">
        <div className="main_container">
          <div className="max-md:bg-white max-md:rounded-2xl max-md:px-4 max-md:py-4">
            {/* Section Heading */}
            <h2 className="md:text-[32px] text-[24px] font-bold text-left md:text-center leading-normal tracking-[-0.42px] md:mb-8 mb-3">
              <span className="text-[#333]">Why Choose </span>
              <span className="text-[#39F]">Miles</span>
              <span className="text-[#333]"> as Your </span>
              <br className="md:hidden block" />
              <span className="text-[#39F]">CMA Institute?</span>
            </h2>

            <p className="text-[#333] md:text-sm text-sm font-normal leading-normal text-left md:text-center max-w-[820px] mx-auto tracking-[-0.42px]">
              Choosing the right CMA institute matters almost as much as passing
              the exam. Miles Education is the world&apos;s largest CMA course
              provider 70,000+ alumni and status as an IMA Diamond Partner.
            </p>

            {/* Why Miles — scrollable row on mobile, 3 + 2 centred on desktop */}
            <div className="flex md:grid md:grid-cols-6 md:gap-6 gap-3 max-w-[1100px] md:mx-auto md:mt-8 max-md:-mx-4 max-md:px-4 max-md:py-4 max-md:scroll-px-4 max-md:mt-1 max-md:overflow-x-auto max-md:snap-x max-md:snap-mandatory max-md:[scrollbar-width:none]">
              {instituteCards?.map((card, idx) => (
                <div
                  key={card.title}
                  className={`flex flex-col overflow-hidden rounded-[12px] [background:linear-gradient(180deg,rgba(255,255,255,0.00)_0%,rgba(255,255,255,0.20)_100%),#FFF] shadow-[0_0_30px_0_rgba(0,0,0,0.10)] md:w-[350px] w-[260px] max-md:shrink-0 max-md:snap-start md:col-span-2 ${
                    idx === 3 ? "md:col-start-2" : ""
                  }`}
                >
                  <Image
                    src={card.image}
                    alt={card.title}
                    width={700}
                    height={420}
                    sizes="(max-width: 767px) 190px, 360px"
                    className="block w-full h-auto"
                    loading="lazy"
                  />
                  <div className="md:p-4 p-3">
                    <h3 className="text-[#3A73AC] md:text-base text-base font-semibold leading-[24px]">
                      {card.title}
                    </h3>
                    <p className="text-[#333] md:text-sm text-sm font-normal leading-[22.75px] md:mt-3 mt-2">
                      {card.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="md:hidden flex justify-center">
              <Image
                src={imageUrl("swipe_text")}
                alt="Swipe to explore"
                width={124}
                height={18}
                sizes="124px"
                className="h-[18px] w-[124px]"
                loading="lazy"
              />
            </div>

            {/* Miles centres */}
            <h3 className="text-[#333] md:text-base text-[13px] font-bold text-left md:text-center md:mt-10 mt-6 md:mb-5 mb-3">
              CMA Classes Near Me: Miles Centres Across India
            </h3>

            <div className="max-w-[930px] mx-auto text-left md:text-center">
              <p className="text-[#333] md:text-sm text-xs font-normal leading-[1.6]">
                If you have been searching for &ldquo;CMA classes near
                me&rdquo;, Miles runs classroom and live-online{" "}
                <a
                  href="https://www.mileseducation.com/cma/best-cma-course-in-bangalore"
                  target="_blank"
                  className="text-[#39F] underline"
                >
                  CMA coaching across Bangalore{" "}
                </a>
                , Mumbai, Delhi, Chennai, Hyderabad, Pune, Ahmedabad, Kochi, and
                Kolkata, backed by the Miles One app wherever you are studying
                from.
              </p>
              <p className="text-[#333] md:text-sm text-xs font-normal leading-[1.6]">
                Prefer to study from home? The same faculty and the same
                resource toolkit are available through CMA online coaching, so
                students in cities without a Miles centre get an identical
                classroom experience. Whether you train in person at a CMA
                institute or online, the syllabus coverage, mock exams, and
                mentorship stay the same.
              </p>
              <p className="text-[#333] md:text-sm text-xs font-normal leading-[1.6] md:mt-5 mt-4">
                Miles Education is more than a CMA institute; it&apos;s a full
                career ecosystem, combining expert-led structured learning,
                personalized 1:1 mentorship and career coaching, and access to a
                70,000-strong alumni and industry network, so you&apos;re
                supported from exam readiness through to placement.
              </p>
              <p className="text-[#333] md:text-sm text-xs font-normal leading-[1.6] md:mt-5 mt-4">
                If you&apos;ve been searching for &ldquo;CMA classes near
                me,&rdquo; Miles runs classroom and live-online CMA coaching
                across Bangalore, Mumbai, Delhi, Chennai, Hyderabad, Pune,
                Ahmedabad, Kochi, and Kolkata, backed by the Miles One app
                wherever you&apos;re studying from. It&apos;s widely recognized
                as among the best{" "}
                <a
                  href="https://www.mileseducation.com/blog/accounting/how-to-find-the-best-cma-institute-in-india"
                  target="_blank"
                  className="text-[#39F] underline"
                >
                  CMA coaching in India
                </a>
                , whether you&apos;d rather train at a CMA institute in India in
                person or through CMA online coaching from anywhere.
              </p>
            </div>

            {/* City selector, centre contact details, map and office gallery */}
            <div className="md:mt-12 mt-6">
              <NewPresence pageName="CMA" hideHeading embedded />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CmaCourseInstitute;
